import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  TextField,
  MenuItem,
  Grid,
  CircularProgress,
  Alert,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Paper,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import RefreshIcon from '@mui/icons-material/Refresh';
import ReplayIcon from '@mui/icons-material/Replay';
import api from '../../services/api';

interface NotificationLogEntry {
  id: string;
  type: string;
  channel: string;
  recipientUserId?: string;
  recipientEmail?: string;
  recipientName?: string;
  eventId?: string;
  status: string;
  errorMessage?: string;
  scheduledFor?: string;
  quietHeld: boolean;
  createdAt: string;
  sentAt?: string;
  dedupeKey?: string;
}

interface LogStats {
  sentToday: number;
  failedToday: number;
  pending: number;
  byType: Array<{ type: string; count: number }>;
}

const STATUS_COLORS: Record<string, 'success' | 'error' | 'warning' | 'info' | 'default'> = {
  sent: 'success',
  failed: 'error',
  pending: 'warning',
  sending: 'info',
};

const CHANNEL_COLORS: Record<string, 'primary' | 'secondary'> = {
  email: 'primary',
  push: 'secondary',
};

const TYPE_LABELS: Record<string, string> = {
  'event-reminder-day-of': 'Day-of Reminder',
  'event-reminder-1-hour': '1-Hour Reminder',
  'shift-reminder-day-of': 'Shift Day-of',
  'shift-reminder-1-hour': 'Shift 1-Hour',
  'feedback-initial': 'Feedback Request',
  'feedback-reminder': 'Feedback Reminder',
  'event-ticket': 'Event Ticket',
  'registration-confirmation': 'Registration Confirmation',
  'check-in': 'Check-in',
  'welcome': 'Welcome',
  'email-code': 'Login Code',
  'general-notification': 'General',
};

const NotificationLogPage: React.FC = () => {
  const [logs, setLogs] = useState<NotificationLogEntry[]>([]);
  const [stats, setStats] = useState<LogStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [total, setTotal] = useState(0);
  const [selectedEntry, setSelectedEntry] = useState<NotificationLogEntry | null>(null);
  const [detailPayload, setDetailPayload] = useState<string | null>(null);
  const [retrying, setRetrying] = useState(false);
  const [retryResult, setRetryResult] = useState<{ success: boolean; message: string } | null>(null);

  // Filters
  const [filterType, setFilterType] = useState('');
  const [filterChannel, setFilterChannel] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterEmail, setFilterEmail] = useState('');

  const fetchStats = useCallback(async () => {
    try {
      const response = await api.get('/notification-engine/log/stats');
      setStats(response.data);
    } catch {
      // Stats are non-critical
    }
  }, []);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params: Record<string, string | number> = {
        page: page + 1,
        limit: rowsPerPage,
      };
      if (filterType) params.type = filterType;
      if (filterChannel) params.channel = filterChannel;
      if (filterStatus) params.status = filterStatus;
      if (filterEmail) params.recipientEmail = filterEmail;

      const response = await api.get('/notification-engine/log', { params });
      setLogs(response.data.logs);
      setTotal(response.data.pagination.total);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load notification log');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, filterType, filterChannel, filterStatus, filterEmail]);

  useEffect(() => {
    fetchLogs();
    fetchStats();
  }, [fetchLogs, fetchStats]);

  const handleViewDetail = async (entry: NotificationLogEntry) => {
    setRetryResult(null);
    try {
      const response = await api.get(`/notification-engine/log/${entry.id}`);
      setSelectedEntry(response.data);
      setDetailPayload(response.data.payload ? JSON.stringify(JSON.parse(response.data.payload), null, 2) : 'No payload');
    } catch {
      setSelectedEntry(entry);
      setDetailPayload('Failed to load payload');
    }
  };

  const handleRefresh = () => {
    fetchLogs();
    fetchStats();
  };

  const handleRetry = async (id: string) => {
    setRetrying(true);
    setRetryResult(null);
    try {
      await api.post(`/notification-engine/log/${id}/retry`);
      setRetryResult({ success: true, message: 'Notification resent successfully' });
      // Update the entry status in the dialog
      setSelectedEntry((prev) => prev ? { ...prev, status: 'sent', errorMessage: undefined } : prev);
      // Refresh the table and stats
      fetchLogs();
      fetchStats();
    } catch (err: any) {
      setRetryResult({ success: false, message: err.response?.data?.error || 'Retry failed' });
    } finally {
      setRetrying(false);
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="h4" fontWeight={600}>
          Notification Log
        </Typography>
        <IconButton onClick={handleRefresh} color="primary">
          <RefreshIcon />
        </IconButton>
      </Box>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Audit trail of all notifications sent through the system
      </Typography>

      {/* Stats Cards */}
      {stats && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={4} sm={4}>
            <Card>
              <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                <Typography variant="body2" color="text.secondary">Sent Today</Typography>
                <Typography variant="h5" fontWeight={700} color="success.main">{stats.sentToday}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={4} sm={4}>
            <Card>
              <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                <Typography variant="body2" color="text.secondary">Failed Today</Typography>
                <Typography variant="h5" fontWeight={700} color="error.main">{stats.failedToday}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={4} sm={4}>
            <Card>
              <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                <Typography variant="body2" color="text.secondary">Pending</Typography>
                <Typography variant="h5" fontWeight={700} color="warning.main">{stats.pending}</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
          <Grid container spacing={2}>
            <Grid item xs={6} sm={3}>
              <TextField
                select
                fullWidth
                size="small"
                label="Type"
                value={filterType}
                onChange={(e) => { setFilterType(e.target.value); setPage(0); }}
              >
                <MenuItem value="">All Types</MenuItem>
                {Object.entries(TYPE_LABELS).map(([key, label]) => (
                  <MenuItem key={key} value={key}>{label}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField
                select
                fullWidth
                size="small"
                label="Channel"
                value={filterChannel}
                onChange={(e) => { setFilterChannel(e.target.value); setPage(0); }}
              >
                <MenuItem value="">All Channels</MenuItem>
                <MenuItem value="email">Email</MenuItem>
                <MenuItem value="push">Push</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField
                select
                fullWidth
                size="small"
                label="Status"
                value={filterStatus}
                onChange={(e) => { setFilterStatus(e.target.value); setPage(0); }}
              >
                <MenuItem value="">All Statuses</MenuItem>
                <MenuItem value="sent">Sent</MenuItem>
                <MenuItem value="failed">Failed</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="sending">Sending</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField
                fullWidth
                size="small"
                label="Email"
                placeholder="Search by email"
                value={filterEmail}
                onChange={(e) => { setFilterEmail(e.target.value); setPage(0); }}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Error */}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Table */}
      <Card>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Time</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Channel</TableCell>
                    <TableCell>Recipient</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {logs.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                        <Typography color="text.secondary">No notifications found</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    logs.map((log) => (
                      <TableRow key={log.id} hover>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          {formatDate(log.createdAt)}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {TYPE_LABELS[log.type] || log.type}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={log.channel}
                            size="small"
                            color={CHANNEL_COLORS[log.channel] || 'default'}
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                            {log.recipientEmail || log.recipientName || log.recipientUserId || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={log.status}
                            size="small"
                            color={STATUS_COLORS[log.status] || 'default'}
                          />
                          {log.quietHeld && (
                            <Chip label="quiet" size="small" variant="outlined" sx={{ ml: 0.5 }} />
                          )}
                        </TableCell>
                        <TableCell align="center">
                          <IconButton size="small" onClick={() => handleViewDetail(log)}>
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[10, 25, 50, 100]}
            />
          </>
        )}
      </Card>

      {/* Detail Dialog */}
      <Dialog
        open={!!selectedEntry}
        onClose={() => { setSelectedEntry(null); setDetailPayload(null); }}
        maxWidth="sm"
        fullWidth
      >
        {selectedEntry && (
          <>
            <DialogTitle>Notification Detail</DialogTitle>
            <DialogContent dividers>
              <Grid container spacing={1.5}>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Type</Typography>
                  <Typography variant="body2">{TYPE_LABELS[selectedEntry.type] || selectedEntry.type}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Channel</Typography>
                  <Typography variant="body2">{selectedEntry.channel}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Status</Typography>
                  <Box><Chip label={selectedEntry.status} size="small" color={STATUS_COLORS[selectedEntry.status] || 'default'} /></Box>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Created</Typography>
                  <Typography variant="body2">{formatDate(selectedEntry.createdAt)}</Typography>
                </Grid>
                {selectedEntry.sentAt && (
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Sent</Typography>
                    <Typography variant="body2">{formatDate(selectedEntry.sentAt)}</Typography>
                  </Grid>
                )}
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Recipient</Typography>
                  <Typography variant="body2">{selectedEntry.recipientEmail || selectedEntry.recipientName || '-'}</Typography>
                </Grid>
                {selectedEntry.errorMessage && (
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">Error</Typography>
                    <Alert severity="error" sx={{ mt: 0.5 }}>{selectedEntry.errorMessage}</Alert>
                  </Grid>
                )}
                {selectedEntry.dedupeKey && (
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">Dedupe Key</Typography>
                    <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>{selectedEntry.dedupeKey}</Typography>
                  </Grid>
                )}
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">Payload</Typography>
                  <Paper variant="outlined" sx={{ p: 1.5, mt: 0.5, maxHeight: 300, overflow: 'auto' }}>
                    <pre style={{ margin: 0, fontSize: '0.8rem', whiteSpace: 'pre-wrap' }}>
                      {detailPayload}
                    </pre>
                  </Paper>
                </Grid>
              </Grid>
            </DialogContent>
            {retryResult && (
              <Alert severity={retryResult.success ? 'success' : 'error'} sx={{ mx: 3, mt: 1 }}>
                {retryResult.message}
              </Alert>
            )}
            <DialogActions>
              {selectedEntry.status === 'failed' && (
                <Button
                  onClick={() => handleRetry(selectedEntry.id)}
                  disabled={retrying}
                  startIcon={retrying ? <CircularProgress size={16} /> : <ReplayIcon />}
                  color="primary"
                  variant="contained"
                  size="small"
                >
                  {retrying ? 'Retrying...' : 'Retry'}
                </Button>
              )}
              <Button onClick={() => { setSelectedEntry(null); setDetailPayload(null); setRetryResult(null); }}>Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default NotificationLogPage;
